import React, { useState } from 'react';

const NAVY = '#1A3A5C', NAVY_DARK = '#0E2238', NAVY_MID = '#1E4A72';
const GOLD = '#F5A623', GOLD_LIGHT = '#FBBF4A', GOLD_PALE = '#FFF8E8';
const GRAY = '#64748B', OFF_WHITE = '#F8F9FC', BORDER = '#E2E8F0', LIGHT_GRAY = '#F1F5F9';
const RED = '#DC2626';

const inputStyle = {
  width: '100%', padding: '12px 16px', border: `1.5px solid ${BORDER}`,
  borderRadius: '8px', fontFamily: "'DM Sans', sans-serif", fontSize: '14px',
  color: '#1E293B', background: '#fff', outline: 'none', transition: 'border-color 0.2s, box-shadow 0.2s',
};

function Field({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '18px' }}>
      <label style={{ fontSize: '12px', fontWeight: 700, color: NAVY_DARK, letterSpacing: '0.3px', textTransform: 'uppercase' }}>{label}</label>
      {children}

      <style>{`
        @media (max-width: 1024px) {
          .contact-grid { grid-template-columns: 1fr !important; }
          .contact-qstrip { grid-template-columns: 1fr 1fr !important; }
          .commit-grid-r { grid-template-columns: 1fr 1fr !important; }
        }
        @media (max-width: 768px) {
          .contact-qstrip { grid-template-columns: 1fr 1fr !important; }
          .form-row-r { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 480px) {
          .contact-qstrip { grid-template-columns: 1fr !important; }
          .commit-grid-r { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

export default function Contact() {
  const [form, setForm] = useState({ name: '', phone: '', email: '', service: '', clientType: '', location: '', message: '' });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const update = key => e => setForm(f => ({ ...f, [key]: e.target.value }));

  const validate = () => {
    const err = {};
    if (!form.name.trim()) err.name = true;
    if (!form.phone.trim()) err.phone = true;
    if (!form.service) err.service = true;
    if (!form.clientType) err.clientType = true;
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = () => { if (validate()) setSubmitted(true); };

  const focusStyle = { borderColor: NAVY, boxShadow: '0 0 0 3px rgba(26,58,92,0.08)' };
  const errStyle = { borderColor: RED };

  return (
    <div style={{ paddingTop: '70px' }}>

      {/* PAGE HERO */}
      <div className="page-hero" style={{ position: 'relative' }}>
        <div style={{ position: 'absolute', width: '500px', height: '500px', borderRadius: '50%', background: 'radial-gradient(circle,rgba(245,166,35,0.07),transparent 70%)', top: '-100px', right: '-100px' }} />
        <div className="page-hero-inner">
          <div className="breadcrumb fade-in"><span>Home</span><span>›</span><span className="cur">Contact Us</span></div>
          <h1 className="fade-in-2">Let's Start Your <span className="accent">Project</span><br />Conversation</h1>
          <p className="fade-in-3">Whether you're planning a solar installation, need an electrical upgrade, or want to secure your premises — our team is ready to guide you from enquiry to completion.</p>
        </div>
      </div>

      {/* QUICK CONTACT STRIP */}
      <div style={{ background: OFF_WHITE, borderBottom: `1px solid ${BORDER}` }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '1px', background: BORDER }}>
          {[
            { icon: '📍', label: 'Location', value: 'MVP Colony, Vizag 530017', sub: '1-32-65, Sector-2, Visakhapatnam' },
            { icon: '📞', label: 'Phone', value: '0891 462252', sub: 'Mon–Sat, 9 AM – 6 PM' },
            { icon: '✉️', label: 'Email', value: 'info@ensignelectrotech.com', sub: 'We reply within 24 hours' },
            { icon: '⚡', label: 'Quick Response', value: 'Free Site Assessment', sub: 'No-obligation consultation' },
          ].map(({ icon, label, value, sub }) => (
            <div key={label} style={{ background: '#fff', padding: '32px 28px', display: 'flex', alignItems: 'flex-start', gap: '16px', transition: 'background 0.2s', cursor: 'default' }}
              onMouseEnter={e => e.currentTarget.style.background = GOLD_PALE}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
              <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: `linear-gradient(135deg, ${NAVY_DARK}, ${NAVY_MID})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', flexShrink: 0 }}>{icon}</div>
              <div>
                <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: GRAY, marginBottom: '4px' }}>{label}</div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: NAVY_DARK, lineHeight: 1.4 }}>{value}</div>
                <div style={{ fontSize: '12px', color: GRAY, marginTop: '2px' }}>{sub}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* MAIN SECTION */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: 'clamp(48px,8vw,96px) clamp(20px,5vw,48px)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.1fr', gap: '72px', alignItems: 'start' }}>

          {/* LEFT */}
          <div>
            <div className="sec-label">Get In Touch</div>
            <h2 className="sec-title">We're Here to Help You Power Forward</h2>
            <p style={{ fontSize: '15px', color: GRAY, lineHeight: 1.8, marginBottom: '36px' }}>
              Fill in the enquiry form and one of our engineers will get back to you within one business day to discuss your project requirements.
            </p>

            {[
              { icon: '📍', title: 'Office Address', lines: ['1-32-65, Ground Floor, Sector-2,', 'MVP Colony, Vizag 530017, Andhra Pradesh'] },
              { icon: '📞', title: 'Phone Number', lines: ['0891 462252', 'Available Monday – Saturday, 9 AM to 6 PM'] },
              { icon: '✉️', title: 'Email Address', lines: ['info@ensignelectrotech.com', 'Responses within one business day'] },
            ].map(({ icon, title, lines }) => (
              <div key={title} style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', padding: '20px', background: OFF_WHITE, borderRadius: '12px', border: `1px solid ${BORDER}`, marginBottom: '16px', transition: 'border-color 0.2s', cursor: 'default' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = GOLD}
                onMouseLeave={e => e.currentTarget.style.borderColor = BORDER}>
                <div style={{ width: '42px', height: '42px', borderRadius: '10px', background: `linear-gradient(135deg, ${GOLD}, #E8900E)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', flexShrink: 0 }}>{icon}</div>
                <div>
                  <strong style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: NAVY_DARK, letterSpacing: '0.3px', marginBottom: '3px' }}>{title}</strong>
                  {lines.map(line => <span key={line} style={{ display: 'block', fontSize: '14px', color: GRAY }}>{line}</span>)}
                </div>
              </div>
            ))}

            {/* Google Maps */}
            <div style={{ borderRadius: '16px', overflow: 'hidden', border: `1px solid ${BORDER}`, height: '220px', marginBottom: '20px' }}>
              <iframe
                title="Ensign Electrotech Location"
                width="100%"
                height="100%"
                style={{ border: 0, display: 'block' }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3800.1!2d83.3232!3d17.7384!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a395b000000000%3A0x0!2sMVP+Colony+Sector+2%2C+Visakhapatnam%2C+Andhra+Pradesh+530017!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin"
              />
            </div>

            {/* Services quick */}
            <div style={{ background: `linear-gradient(135deg, ${NAVY_DARK}, ${NAVY})`, borderRadius: '16px', padding: '28px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: 600, color: GOLD, marginBottom: '16px', letterSpacing: '0.3px' }}>We Can Help You With</h4>
              {['Grid-Connected Rooftop & Ground-Mounted Solar Systems','Solar EPC — Engineering, Procurement & Construction','Net Metering Applications & DISCOM Coordination','Electrical Installations & Power Distribution','CCTV, Access Control & Security System Design','Annual Maintenance Contracts (AMC)'].map(item => (
                <div key={item} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: 'rgba(255,255,255,0.75)', marginBottom: '10px' }}>
                  <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: GOLD, flexShrink: 0 }} />
                  {item}
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — FORM */}
          <div style={{ background: '#fff', borderRadius: '20px', border: `1px solid ${BORDER}`, padding: '40px', boxShadow: '0 4px 24px rgba(26,58,92,0.06)' }}>
            {!submitted ? (
              <>
                <div style={{ marginBottom: '28px' }}>
                  <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: '24px', fontWeight: 700, color: NAVY_DARK, marginBottom: '6px' }}>Send Us an Enquiry</h3>
                  <p style={{ fontSize: '13px', color: GRAY }}>Tell us about your project and we'll get back to you with a tailored proposal.</p>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <Field label="Full Name *">
                    <input style={{ ...inputStyle, ...(errors.name ? errStyle : {}) }} placeholder="Your full name" value={form.name} onChange={update('name')}
                      onFocus={e => Object.assign(e.target.style, focusStyle)} onBlur={e => Object.assign(e.target.style, { borderColor: errors.name ? RED : BORDER, boxShadow: 'none' })} />
                  </Field>
                  <Field label="Phone Number *">
                    <input style={{ ...inputStyle, ...(errors.phone ? errStyle : {}) }} placeholder="0891 462252" value={form.phone} onChange={update('phone')}
                      onFocus={e => Object.assign(e.target.style, focusStyle)} onBlur={e => Object.assign(e.target.style, { borderColor: errors.phone ? RED : BORDER, boxShadow: 'none' })} />
                  </Field>
                </div>

                <Field label="Email Address">
                  <input style={inputStyle} type="email" placeholder="your@email.com" value={form.email} onChange={update('email')}
                    onFocus={e => Object.assign(e.target.style, focusStyle)} onBlur={e => Object.assign(e.target.style, { borderColor: BORDER, boxShadow: 'none' })} />
                </Field>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <Field label="Service Required *">
                    <select style={{ ...inputStyle, ...(errors.service ? errStyle : {}), appearance: 'none', cursor: 'pointer' }} value={form.service} onChange={update('service')}
                      onFocus={e => Object.assign(e.target.style, focusStyle)} onBlur={e => Object.assign(e.target.style, { borderColor: errors.service ? RED : BORDER, boxShadow: 'none' })}>
                      <option value="" disabled>Select a service</option>
                      <option>☀️ Rooftop Solar Installation</option>
                      <option>☀️ Ground-Mounted Solar Plant</option>
                      <option>☀️ Solar O&M / AMC</option>
                      <option>⚡ Electrical Installation</option>
                      <option>⚡ Power Distribution System</option>
                      <option>🔐 CCTV / Surveillance</option>
                      <option>🔐 Access Control System</option>
                      <option>🔐 Integrated Security</option>
                      <option>🔧 General Enquiry</option>
                    </select>
                  </Field>
                  <Field label="Client Type *">
                    <select style={{ ...inputStyle, ...(errors.clientType ? errStyle : {}), appearance: 'none', cursor: 'pointer' }} value={form.clientType} onChange={update('clientType')}
                      onFocus={e => Object.assign(e.target.style, focusStyle)} onBlur={e => Object.assign(e.target.style, { borderColor: errors.clientType ? RED : BORDER, boxShadow: 'none' })}>
                      <option value="" disabled>Select type</option>
                      <option>🏠 Residential</option>
                      <option>🏢 Commercial</option>
                      <option>🏭 Industrial</option>
                      <option>🏛️ Government / Institution</option>
                    </select>
                  </Field>
                </div>

                <Field label="Project Location">
                  <input style={inputStyle} placeholder="District / City, Andhra Pradesh" value={form.location} onChange={update('location')}
                    onFocus={e => Object.assign(e.target.style, focusStyle)} onBlur={e => Object.assign(e.target.style, { borderColor: BORDER, boxShadow: 'none' })} />
                </Field>

                <Field label="Project Details">
                  <textarea style={{ ...inputStyle, minHeight: '100px', lineHeight: 1.6, resize: 'none' }} placeholder="Briefly describe your requirements — system size, current electricity bill, no. of floors, special requirements, etc." value={form.message} onChange={update('message')}
                    onFocus={e => Object.assign(e.target.style, focusStyle)} onBlur={e => Object.assign(e.target.style, { borderColor: BORDER, boxShadow: 'none' })} />
                </Field>

                <button style={{ width: '100%', padding: '15px', background: `linear-gradient(135deg, ${NAVY_DARK}, ${NAVY})`, color: '#fff', fontFamily: "'DM Sans', sans-serif", fontSize: '15px', fontWeight: 600, border: 'none', borderRadius: '10px', cursor: 'pointer', letterSpacing: '0.3px', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(26,58,92,0.2)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
                  onClick={handleSubmit}>
                  <span>📨</span> Send Enquiry — We'll Respond Within 24 Hours
                </button>
                <div style={{ textAlign: 'center', fontSize: '12px', color: GRAY, marginTop: '12px' }}>🔒 Your information is confidential and will never be shared.</div>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '48px 24px' }}>
                <div style={{ fontSize: '56px', marginBottom: '16px' }}>✅</div>
                <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: '24px', fontWeight: 700, color: NAVY_DARK, marginBottom: '8px' }}>Enquiry Received!</h3>
                <p style={{ fontSize: '14px', color: GRAY, lineHeight: 1.7, marginBottom: '24px' }}>Thank you for reaching out to Ensign Electrotech LLP. Our engineering team will review your requirements and contact you within one business day.</p>
                <button style={{ background: GOLD, color: NAVY_DARK, fontSize: '13px', fontWeight: 600, padding: '11px 26px', borderRadius: '8px', cursor: 'pointer', border: 'none', transition: 'background 0.2s' }}
                  onMouseEnter={e => e.target.style.background = GOLD_LIGHT}
                  onMouseLeave={e => e.target.style.background = GOLD}
                  onClick={() => { setSubmitted(false); setForm({ name:'',phone:'',email:'',service:'',clientType:'',location:'',message:'' }); setErrors({}); }}>
                  ← Send Another Enquiry
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* COMMITMENT STRIP */}
      <div style={{ background: OFF_WHITE, borderTop: `1px solid ${BORDER}`, padding: 'clamp(36px,5vw,56px) clamp(20px,5vw,48px)' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center' }}>
          <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: '26px', fontWeight: 700, color: NAVY_DARK, marginBottom: '8px' }}>Our Commitment to You</h3>
          <p style={{ fontSize: '14px', color: GRAY, maxWidth: '480px', margin: '0 auto 36px' }}>Every client interaction is guided by our core values of transparency, professionalism, and technical excellence.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '20px' }}>
            {[['⚡','Fast Response','We respond to every enquiry within one business day — guaranteed.'],['🆓','Free Site Assessment','No-obligation site visit and technical feasibility study at no cost.'],['📋','Detailed Proposal','Receive a clear, itemised proposal with system specs and project timeline.'],['🤝','No Pressure','We earn your trust through information — not just your signature.']].map(([icon,title,desc]) => (
              <div key={title} style={{ background: '#fff', borderRadius: '12px', padding: '24px', border: `1px solid ${BORDER}`, textAlign: 'center' }}>
                <div style={{ fontSize: '28px', marginBottom: '10px' }}>{icon}</div>
                <div style={{ fontSize: '13px', fontWeight: 600, color: NAVY_DARK, marginBottom: '4px' }}>{title}</div>
                <div style={{ fontSize: '12px', color: GRAY, lineHeight: 1.5 }}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`@keyframes pinBounce{0%,100%{transform:translateY(0);}50%{transform:translateY(-8px);}}`}</style>

      <style>{`
        @media (max-width: 1024px) {
          .contact-grid { grid-template-columns: 1fr !important; }
          .contact-qstrip { grid-template-columns: 1fr 1fr !important; }
          .commit-grid-r { grid-template-columns: 1fr 1fr !important; }
        }
        @media (max-width: 768px) {
          .contact-qstrip { grid-template-columns: 1fr 1fr !important; }
          .form-row-r { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 480px) {
          .contact-qstrip { grid-template-columns: 1fr !important; }
          .commit-grid-r { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
